import React from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { NavLink, useLocation } from "react-router-dom";
import { Icon } from "@qgiv/core-react";
import { Divider } from "@qgiv/core-donor";
import { useDispatch } from "react-redux";
import { setAppShowRemainingForm } from "../../../../../redux/slices/appSettingsSlice";
import "./RegistrationNav.scss";

const RegistrationNavItemCms = ({ name }) => (
    <span className="registration-nav__cms-link">{name}</span>
);

RegistrationNavItemCms.propTypes = {
    name: PropTypes.string.isRequired,
};

const RegistrationNavItem = ({
    route,
    location,
    baseUrl,
    getCurrentRegistrationRoleHasBeenCompleted,
}) => {
    const dispatch = useDispatch();

    // When Registration_Role or team is deselected, clicking the nav should not show the remaining form
    const { values = {} } = useFormikContext();

    const currentRegistrationRoleHasBeenCompleted =
        getCurrentRegistrationRoleHasBeenCompleted(values);

    const storeWasVisited = !!(
        location.pathname === `${baseUrl}/form/payment` &&
        route.path === `${baseUrl}/form/store`
    );

    const handleRouteClick = (e, _route) => {
        if (!_route.stepComplete && !storeWasVisited) {
            e.preventDefault();
        }

        if (
            currentRegistrationRoleHasBeenCompleted &&
            _route.stepComplete &&
            _route.path === `${baseUrl}/form/registration`
        ) {
            dispatch(setAppShowRemainingForm(true));
        }
    };

    return (
        <NavLink
            onClick={(e) => handleRouteClick(e, route)}
            to={route.path}
            className={`registration-nav__a${
                route.stepComplete || storeWasVisited
                    ? " registration-nav__a--complete"
                    : ""
            }`}
            activeclassname="registration-nav__a--active"
        >
            {(route.stepComplete || storeWasVisited) &&
                location.pathname !== route.path && (
                    <Icon
                        className="registration-nav__icon"
                        glyph="check-circle-solid"
                        type="FontAwesome"
                    />
                )}
            <small>{route.name}</small>
        </NavLink>
    );
};

RegistrationNavItem.propTypes = {
    route: PropTypes.shape({
        displayRoute: PropTypes.bool.isRequired,
        name: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
        stepComplete: PropTypes.bool.isRequired,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
    baseUrl: PropTypes.string.isRequired,
    getCurrentRegistrationRoleHasBeenCompleted: PropTypes.func.isRequired,
};

const RegistrationNav = ({
    baseUrl,
    getCurrentRegistrationRoleHasBeenCompleted,
    isCms,
    isMediumScreen,
    routes,
}) => {
    // -------------------------------------------------------------------------
    // TODO: Fix this conditional use of hooks, possibly by adding
    // a nullish coalescing
    // const location = useLocation() ?? undefined;
    // -------------------------------------------------------------------------
    // Cms View will throw errors since not wrapped in <Router>

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const location = isCms ? undefined : useLocation();

    return (
        <div
            className={`registration-nav -margin-top--10 ${
                !isMediumScreen ? "registration-nav__padding" : ""
            }`}
        >
            <Divider classNames="registration-nav__divider divider--no-margin -display--block" />
            <nav className="registration-nav__nav">
                <ul
                    className={`registration-nav__ul ${
                        isMediumScreen ? "-padding--5" : "-padding--10"
                    }`}
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role="nav"
                >
                    {routes.map(
                        (route, i) =>
                            route.displayRoute && (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={i} className="registration-nav__li">
                                    {isCms ? (
                                        <RegistrationNavItemCms
                                            name={route.name}
                                        />
                                    ) : (
                                        <RegistrationNavItem
                                            route={route}
                                            location={location}
                                            baseUrl={baseUrl}
                                            getCurrentRegistrationRoleHasBeenCompleted={
                                                getCurrentRegistrationRoleHasBeenCompleted
                                            }
                                        />
                                    )}
                                    {i < routes.length - 1 && (
                                        <Icon
                                            className="registration-nav__separator"
                                            glyph="chevron-right-solid"
                                            type="FontAwesome"
                                        />
                                    )}
                                </li>
                            ),
                    )}
                </ul>
            </nav>
            {!isMediumScreen && (
                <Divider classNames="registration-nav__divider divider--no-margin -display--block" />
            )}
        </div>
    );
};

RegistrationNav.propTypes = {
    baseUrl: PropTypes.string.isRequired,
    getCurrentRegistrationRoleHasBeenCompleted: PropTypes.func.isRequired,
    isCms: PropTypes.bool.isRequired,
    isMediumScreen: PropTypes.bool.isRequired,
    routes: PropTypes.arrayOf(
        PropTypes.shape({
            displayRoute: PropTypes.bool.isRequired,
            name: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
            stepComplete: PropTypes.bool.isRequired,
        }).isRequired,
    ).isRequired,
};

export default RegistrationNav;
